import { Injectable, computed, inject, signal } from '@angular/core';
import { VersionService } from './version.service';

/**
 * Why a lazy route's chunk could not be fetched.
 *
 * - `stale-tab` — the server has moved on since this tab booted. Its `index.html` names hashed
 *   chunks the server has since replaced, and a reload genuinely fixes it.
 * - `incomplete-build` — the server is still serving the release this tab booted from, and that
 *   release does not have the chunk. A reload re-requests the very same absent URL.
 * - `unknown` — the probe that separates the two could not run or could not decide.
 */
export type ChunkFailureReason = 'unknown' | 'stale-tab' | 'incomplete-build';

/** How long the deployment probe may take before the banner stops waiting on it. */
const PROBE_TIMEOUT_MS = 4_000;

/** A module asset whose name carries a content hash — the only kind that can tell builds apart. */
const CONTENT_HASHED_ASSET = /-[A-Z0-9]{6,}\.js$/;

/**
 * Every module asset the ENTRY DOCUMENT names, as a fingerprint of one build — or `''` when the
 * document names nothing content-hashed and so cannot identify a build at all.
 *
 * This is a *corroborating* signal only, never the whole verdict, because it can be far weaker than
 * it looks on a build whose entry bundle is not content-hashed. Such a build emits an unhashed
 * `main.js` plus ten hashed `modulepreload` chunks — and those ten are the vendor/framework initial
 * graph, invariant to application changes: a build of this tree emitted byte-identical preload names
 * to the ones dev.lendox.com was serving three releases earlier. An app-only release then changes
 * only the CONTENT of `main.js`, whose name never changes, and the fingerprint alone would call a
 * genuinely stale tab an incomplete build. The release version carries that half of the evidence;
 * see {@link NavigationFailureService}.
 *
 * EM-1796 removed that weakness from every DEPLOYED build — `production`, `uat` and `docker-dev`
 * all set `outputHashing: "all"`, so `main-<HASH>.js` moves on every release and the image build
 * refuses an output where it does not. Only `ng serve` (`development`) still emits `main.js`, and
 * no deployment probe runs there. The fingerprint is therefore stronger than this paragraph
 * describes on any environment a user can reach — it stays a corroborating signal because the
 * release version is the cheaper and more direct evidence, not because it cannot identify a build.
 */
export function entryBundleFingerprint(markup: string): string {
  const names = [...new Set(markup.match(/\b(?:chunk|main|polyfills)-?[A-Z0-9]*\.js\b/g) ?? [])];
  if (!names.some((name) => CONTENT_HASHED_ASSET.test(name))) {
    return '';
  }
  return names.sort().join('|');
}

/**
 * Records router navigation failures so the app can SAY something instead of doing nothing
 * (EM-1634), and says something TRUE about what a reload will achieve (EM-1641).
 *
 * ## The defect this exists for
 * Every page behind a lazy route is fetched as a hashed chunk. When that fetch fails the router
 * emits a `NavigationError`, the navigation is abandoned — and, with no error handler registered,
 * nothing else happens at all. The user presses **Open** and the application does not move, does
 * not warn, and leaves only a console line. On the v1.3.104 dev build that is exactly how a missing
 * `chunk-*.js` presented: two financing requests stuck at Stage 15 with a button that appeared
 * simply not to work, and the real reason (`Failed to fetch dynamically imported module`) visible
 * only to someone who opened devtools.
 *
 * ## EM-1641 — why the banner alone was not enough
 * EM-1634 shipped one message for both causes: *"the application has been updated since this tab
 * was opened. Reload to get the current version."* Eight days later the lender's **Place Offer**
 * button hit the same wall and reloading did not help, so the user was left with a confident
 * instruction, no explanation of why it failed, and nothing to report.
 *
 * The banner now distinguishes the two causes — but note what it does NOT do with the answer: it
 * never withholds the reload. The verdict changes only the WORDING, adding "and if it still fails
 * after reloading, this part is missing from the deployed build — report it". That asymmetry is
 * deliberate. Being wrong about `stale-tab` costs the user one wasted click; being wrong about
 * `incomplete-build` while hiding the Reload button would strand someone whose reload would have
 * worked — recreating this very bug from the other side. A remedy is never taken away on the
 * strength of an inference.
 *
 * ## How the verdict is reached (Rule 25 — the mechanism, not a guess)
 * Two independent signals, and `incomplete-build` needs BOTH to say "nothing changed":
 *
 * 1. **The release version.** `version.json` is the portal's own release identity, bumped on every
 *    portal commit and served `no-cache` by design (EM-1625). {@link VersionService} reads it once
 *    at boot — that snapshot is what this tab is running — and the probe re-reads it. A different
 *    version means the server has moved on, full stop.
 * 2. **The entry document's module names**, as a corroborating check for the case where the version
 *    is unavailable on one side. It cannot carry the verdict alone (see
 *    {@link entryBundleFingerprint}).
 *
 * Anything unresolved — probe failed, timed out, no version on either side — stays `unknown`, which
 * reads exactly like the pre-EM-1641 behaviour.
 */
@Injectable({ providedIn: 'root' })
export class NavigationFailureService {
  /**
   * Injected for its BOOT snapshot of `version.json`, not for display.
   *
   * `VersionService` reads the file once, in its constructor, and never again — so the value it
   * holds is "the release this tab started on" precisely because this service is itself constructed
   * at bootstrap (`AppComponent` injects it), which forces that read to happen then rather than
   * whenever some later screen first wants a version badge. Reusing it also keeps `version.json` to
   * a single reader (Rule 17).
   */
  private readonly versionService = inject(VersionService);

  private readonly _chunkLoadFailed = signal(false);
  private readonly _lastFailedUrl = signal<string | null>(null);
  private readonly _reason = signal<ChunkFailureReason>('unknown');

  /**
   * Which failure the in-flight probe belongs to. A probe started for a banner the user has since
   * dismissed (or replaced with a newer failure) must not write its verdict back — `dismiss()` would
   * be silently undone, leaving `reason()` claiming `incomplete-build` for a banner that is gone.
   */
  private probeGeneration = 0;

  /** True once a lazy route's chunk failed to load. Drives the root banner. */
  readonly chunkLoadFailed = this._chunkLoadFailed.asReadonly();

  /** The URL the user was trying to reach, for the banner's message. */
  readonly lastFailedUrl = this._lastFailedUrl.asReadonly();

  /** Why the chunk could not be fetched, once the probe has settled it. */
  readonly reason = this._reason.asReadonly();

  /**
   * True when the evidence says this tab is already on the release the server is serving — so a
   * reload is unlikely to change anything and the failure is worth reporting.
   *
   * It selects a WORDING, never whether the reload is offered. See the class docblock.
   */
  readonly looksLikeIncompleteBuild = computed(() => this._reason() === 'incomplete-build');

  /**
   * Classifies a navigation error and, when it is a chunk-load failure, raises the banner.
   *
   * The matched strings are the browser-specific wordings for one condition — a dynamic `import()`
   * whose module could not be fetched. They differ per engine and are not exposed as a code, so
   * matching the message is the available signal:
   *   - Chrome/Edge: `Failed to fetch dynamically imported module: <url>`
   *   - Firefox:     `error loading dynamically imported module`
   *   - Safari:      `Importing a module script failed.`
   *   - bundler-wrapped: `ChunkLoadError`
   *
   * Anything else is left alone deliberately: a guard rejecting or a resolver throwing is a
   * different failure with a different remedy, and telling that user to reload would be wrong.
   *
   * The banner is raised SYNCHRONOUSLY — the user must be told immediately that the click went
   * nowhere, and the reload is offered from that first frame. The probe only sharpens the wording.
   */
  recordNavigationError(error: unknown, url: string | null = null): void {
    if (!this.isChunkLoadFailure(error)) {
      return;
    }
    this._lastFailedUrl.set(url);
    this._reason.set('unknown');
    this._chunkLoadFailed.set(true);
    void this.classifyDeployment(++this.probeGeneration);
  }

  /** Dismisses the banner without reloading — the user's call. */
  dismiss(): void {
    this._chunkLoadFailed.set(false);
    this._reason.set('unknown');
    // Abandons any probe still in flight, so a late answer cannot resurrect the verdict.
    this.probeGeneration += 1;
  }

  /**
   * Full reload of the current document, picking up the server's current `index.html`.
   *
   * Not `router.navigate` — the stale `index.html` is the problem, and an in-app navigation would
   * keep it.
   */
  reload(): void {
    window.location.reload();
  }

  /**
   * Settles {@link reason} from what the server is serving right now.
   *
   * Deliberately best-effort and silent: this runs on an error path, and a probe that throws or
   * hangs must never be worse than no probe. Every unresolved case leaves the reason `unknown`.
   */
  private async classifyDeployment(generation: number): Promise<void> {
    const [servedVersion, servedMarkup] = await Promise.all([
      this.fetchText('version.json'),
      this.fetchText('index.html'),
    ]);
    if (generation !== this.probeGeneration) {
      return;
    }

    const bootVersion = this.versionService.version();
    const currentVersion = this.parseVersion(servedVersion);
    if (bootVersion !== '' && currentVersion !== '' && bootVersion !== currentVersion) {
      // The release changed under this tab. Nothing else needs checking.
      this._reason.set('stale-tab');
      return;
    }

    const served = servedMarkup === null ? '' : entryBundleFingerprint(servedMarkup);
    const running = entryBundleFingerprint(this.runningEntryMarkup());
    if (served !== '' && running !== '' && served !== running) {
      this._reason.set('stale-tab');
      return;
    }

    // Only a POSITIVE version match justifies the sharper wording. Two identical-looking entry
    // documents are not enough on their own: on dev/UAT their hashed names are vendor chunks that
    // do not move when the application does.
    if (bootVersion !== '' && currentVersion !== '' && bootVersion === currentVersion) {
      this._reason.set('incomplete-build');
    }
  }

  /** `version.json`'s `version` field, or `''` when it cannot be read. */
  private parseVersion(body: string | null): string {
    if (body === null) {
      return '';
    }
    try {
      return String((JSON.parse(body) as { version?: unknown })?.version ?? '');
    } catch {
      return '';
    }
  }

  /**
   * A same-origin file as the server has it right now, bypassing every cache, or `null`.
   *
   * Resolved against `<base href>` rather than the origin root so the probe follows the app wherever
   * it is mounted, and bounded by {@link PROBE_TIMEOUT_MS}: a server already failing to serve one
   * bundle is exactly the situation where a request can hang, and a hung probe would pin the banner
   * on `unknown` forever.
   */
  private async fetchText(path: string): Promise<string | null> {
    try {
      const url = new URL(path, document.baseURI).toString();
      const response = await fetch(url, {
        cache: 'no-store',
        credentials: 'same-origin',
        signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
      });
      return response.ok ? await response.text() : null;
    } catch {
      return null;
    }
  }

  /**
   * The module assets of the document this tab is running.
   *
   * Read from the live DOM: these elements are exactly what the browser used to boot the app, and
   * Angular never rewrites them. Verified against a real built artefact rather than assumed
   * (Rule 25) — the live DOM carries exactly the entry document's module assets and does not drift
   * after in-app navigation.
   *
   * Should that ever stop holding — an edge that rewrites `index.html`, a runtime-injected module
   * script — the two fingerprints simply stop matching and every failure reads as `stale-tab`, i.e.
   * the pre-EM-1641 wording. That is why this signal only ever corroborates and never decides alone.
   */
  private runningEntryMarkup(): string {
    const nodes = document.querySelectorAll<HTMLElement>(
      'script[type="module"][src], link[rel="modulepreload"][href]',
    );
    return Array.from(nodes)
      .map((el) => el.getAttribute('src') ?? el.getAttribute('href') ?? '')
      .join('\n');
  }

  private isChunkLoadFailure(error: unknown): boolean {
    const message =
      error instanceof Error
        ? `${error.name} ${error.message}`
        : typeof error === 'string'
          ? error
          : String((error as { message?: unknown })?.message ?? '');

    return /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|chunkloaderror/i.test(
      message,
    );
  }
}
